/*
 * Copyright (c) 2023. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import { alpha, type Theme } from '@mui/material';
import {
  type AbsoluteURL,
  allProjectFlows,
  type FormattedNotification,
  formatBidRequestNotification,
  formatDateLocal,
  formatUserStr,
  gisFlows,
  NotificationSeverity,
  type NotificationsQueryResult,
  type ObjectIdMap,
  type PANotification,
  type PrefectFlow,
  PricesType,
  paBatchFlows,
  paFlows,
  type UINotification,
} from '@poweralpha/common';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime.js';
import { find, orderBy, upperCase, upperFirst } from 'lodash';
import { flowNameToLogURL, serviceNameToResultURL } from '../simulation-status/routes';
import { getLogsLink, getScenarioLink } from '../simulation-status/utils';
import {
  componentCreationFlows,
  componentCreationFlowToBaseUrls,
  componentCreationFlowToDetailUrls,
  notLoadedDefault,
} from './const';

dayjs.extend(relativeTime);

const getFallbackNotificationLink = (notification: PANotification): AbsoluteURL => {
  const bidRequest = notification.meta.bid_request;
  if (bidRequest) {
    const versionSearch: `?${string}` | '' =
      bidRequest.version !== undefined ? `?version=${bidRequest.version}` : '';
    return `/bid-requests/${bidRequest.id}/bid-matrix${versionSearch}`;
  }

  return '/notifications';
};

const getFallbackUserStr = (notification: PANotification, userEmail: string | undefined) => {
  try {
    return formatUserStr(notification.created_by, { userEmail });
  } catch (error) {
    // biome-ignore lint/suspicious/noConsole: keep bad notification payloads visible without crashing UI
    console.error('Unable to format notification user', notification, error);
    return 'Someone';
  }
};

const formatFallbackNotification = (
  notification: PANotification,
  userEmail: string | undefined,
): FormattedNotification => {
  const targetedMessage = notification.meta.bid_request?.targeted_message;
  const subject =
    notification.meta.bid_request?.name ?? notification.workflow_name ?? 'Notification';
  const fallbackMessage =
    targetedMessage ?? 'Open the notification center to review this notification.';

  return {
    link: getFallbackNotificationLink(notification),
    subject,
    notif_content: `${getFallbackUserStr(notification, userEmail)} sent a notification. ${fallbackMessage}`,
    message: notification.message ?? null,
    whenStr: dayjs(notification.created_at).fromNow(),
  };
};

const componentCreationCustomErrorResponse: Partial<
  Record<PrefectFlow, (n: PANotification) => { message: string; link: AbsoluteURL } | undefined>
> = {
  'Add Price Curves': (n) => {
    if (!n.meta.column_index) return undefined;
    return {
      message: `skipped a ${
        n.meta.prices_type === PricesType.REC
          ? upperCase(PricesType.REC)
          : upperFirst(n.meta.prices_type)
      } record due to a validation error (column index: ${n.meta.column_index})`,
      link: '/notifications',
    };
  },
};

const formatPlantSimBatchNotification = (
  notification: PANotification,
  projectNames: Array<string> | undefined,
) => {
  const { meta } = notification;

  let link = serviceNameToResultURL.plant_sim_batch;
  let subject = `${notification.workflow_name} Simulate`;
  let message = `${notification.workflow_name} simulation started on ${formatDateLocal(
    meta.started,
  )}${projectNames ? ` for ${projectNames[0]}` : ''}`;
  if (notification.severity === NotificationSeverity.ERROR) {
    subject = `${subject} Failed`;
    message = `${message} failed due to an error.`;
    link = flowNameToLogURL.PlantSim;
  } else if (notification.severity === NotificationSeverity.WARNING && meta.num_of_errors) {
    subject = `${subject} Successful with Warnings`;
    message = `${message} completed, with ${meta.num_of_errors} run${
      meta.num_of_errors > 1 ? 's' : ''
    } failing.`;
  } else if (notification.severity === NotificationSeverity.SUCCESS) {
    subject = `${subject} Successful`;
    message = `${message} completed successfully.`;
  } else {
    throw new Error('Not implemented');
  }

  return { message, link, subject, possessive: true };
};

const formatProjectBatchNotification = (
  notification: PANotification,
  projectNames: Array<string> | undefined,
) => {
  const { meta } = notification;

  let link: AbsoluteURL;
  let subject = `${projectNames?.at(0)} Batch`;
  let message = `batch simulation for ${projectNames?.at(0)} started on ${formatDateLocal(
    meta.started,
  )}`;
  if (notification.severity === NotificationSeverity.ERROR) {
    subject = `${subject} Failed`;
    message = `${message} failed due to an error.`;
    link = `/projects/${meta.project_id}/logs`;
  } else if (
    notification.severity === NotificationSeverity.WARNING &&
    meta.num_of_errors &&
    meta.batch_workflow_id
  ) {
    link = `/projects/${meta.project_id}/batch/${meta.batch_workflow_id}/`;
    subject = `${subject} Successful with Warnings`;
    message = `${message} completed, with ${meta.num_of_errors} run${
      meta.num_of_errors > 1 ? 's' : ''
    } failing.`;
  } else if (notification.severity === NotificationSeverity.SUCCESS) {
    subject = `${subject} Successful`;
    link = `/projects/${meta.project_id}/batch/${meta.batch_workflow_id}/`;
    message = `${message} completed successfully.`;
  } else {
    throw new Error('Not implemented');
  }

  return { message, link, subject: `${subject} (${notification.workflow_name})`, possessive: true };
};

const formatProjectSimNotification = (
  notification: PANotification,
  projectNames: Array<string> | undefined,
) => {
  const { meta } = notification;

  let link: AbsoluteURL;
  let subject = `${projectNames?.at(0)} Simulation`;
  let message = `simulation for ${projectNames?.at(0)} started on ${formatDateLocal(meta.started)}`;
  if (notification.severity === NotificationSeverity.ERROR) {
    subject = `${subject} Failed`;
    message = `${message} failed due to an error.`;
    link = `/projects/${meta.project_id}/logs`;
  } else if (notification.severity === NotificationSeverity.SUCCESS && meta.scenario_id) {
    subject = `${subject} Successful`;
    link = `/projects/${meta.project_id}/results/${meta.scenario_id}/`;
    message = `${message} completed successfully.`;
  } else {
    throw new Error('Not implemented');
  }

  return { message, link, subject: `${subject} (${notification.workflow_name})`, possessive: true };
};

const formatSingleRunNotification = (
  notification: PANotification,
  projectNames: Array<string> | undefined,
) => {
  const { meta } = notification;

  let link: AbsoluteURL;
  let message: string;
  let subject = `${notification.workflow_name} Simulate`;
  const baseMsg = `${notification.workflow_name} simulation started on ${formatDateLocal(
    meta.started,
  )}`;

  if (notification.severity === NotificationSeverity.ERROR) {
    subject = `${subject} Failed`;
    message = `${baseMsg} failed due to an error.`;
    link = getLogsLink(notification.workflow_name, meta.project_id);
  } else if (notification.severity === NotificationSeverity.WARNING && notification.message) {
    message = `${baseMsg} encountered a warning.`;
    subject = `${subject}: Warning`;
    link = getScenarioLink(notification.workflow_name, meta.scenario_id ?? '');
  } else if (notification.severity === NotificationSeverity.SUCCESS) {
    subject = `${subject} Successful`;
    link = getScenarioLink(notification.workflow_name, meta.scenario_id ?? '');
    message = `${baseMsg} completed successfully.`;
  } else {
    throw new Error('Not implemented');
  }

  return {
    message,
    link,
    subject: `${subject}${projectNames ? ` [${projectNames[0]}]` : ''}`,
    possessive: true,
  };
};

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: refactor
const formatRecordCreationNotification = (notification: PANotification) => {
  const { meta } = notification;

  if (!notification.workflow_name) {
    throw new Error('Workflow name not defined');
  }

  const baseUrl = componentCreationFlowToBaseUrls[notification.workflow_name];
  const customDetailUrl = componentCreationFlowToDetailUrls[notification.workflow_name];

  let link = customDetailUrl ? customDetailUrl(notification) : baseUrl;
  let message = `flow '${notification.workflow_name}' started on ${formatDateLocal(meta.started)}`;
  if (meta.existing_id) {
    if (notification.severity === NotificationSeverity.WARNING) {
      message = `${message} skipped a duplicate record.`;
    } else if (notification.severity === NotificationSeverity.ERROR) {
      message = `${message} failed due to a duplicate record.`;
    }

    link += `${meta.existing_id}`;
  } else if (notification.message) {
    const customCallback = componentCreationCustomErrorResponse[notification.workflow_name];
    const customRes = customCallback ? customCallback(notification) : undefined;
    message = `${message} ${customRes?.message ?? 'failed due to an error'}.`;
    link = customRes?.link ?? '/db/logs';
  } else {
    const recordPlural = (meta.num_successes ?? 0) > 1 ? 's' : '';
    message = `${message} completed successfully${
      meta.num_successes ? `, with ${meta.num_successes} record${recordPlural} saved` : ''
    }.`;
  }

  return {
    message,
    link: link as AbsoluteURL,
    subject: notification.workflow_name,
    possessive: true,
  };
};

type NotifFormatFn = (
  notif: PANotification,
  projectNames: Array<string> | undefined,
) => {
  link: AbsoluteURL;
  message: string;
  subject: string;
  possessive?: boolean;
};

const getFormatter = (
  notification: PANotification,
  projectNames: Array<string> | undefined,
): NotifFormatFn | undefined => {
  const { meta } = notification;
  if (meta.bid_request && projectNames) {
    return formatBidRequestNotification; // bid request
  }

  if (!notification.workflow_name) {
    throw new Error('Workflow name must be defined for workflow notifications');
  }

  if (notification.workflow_name === 'PlantSim Batch' && meta.started) {
    return formatPlantSimBatchNotification; // plantsim batch
  }

  if (projectNames && meta.started && allProjectFlows.includes(notification.workflow_name)) {
    if (paBatchFlows.includes(notification.workflow_name)) {
      return formatProjectBatchNotification; // project batch
    }
    if (
      paFlows.includes(notification.workflow_name) ||
      gisFlows.includes(notification.workflow_name)
    ) {
      return formatProjectSimNotification; // single-run project
    }
  }

  if (componentCreationFlows.includes(notification.workflow_name)) {
    return formatRecordCreationNotification; // record creation run
  }

  if (meta.started) {
    return formatSingleRunNotification; // single service run
  }

  return undefined;
};

export const formatNotification = (
  notification: PANotification,
  projectIdMap: Record<string, string>,
  userEmail: string | undefined,
): FormattedNotification => {
  const { meta } = notification;

  let projectNames: Array<string> | undefined;
  if (Array.isArray(meta.project_id)) {
    projectNames = meta.project_id.map((id) => projectIdMap[id]);
  } else {
    projectNames = meta.project_id ? [projectIdMap[meta.project_id]] : undefined;
  }

  const error = notification.message ?? undefined;
  const formatter = getFormatter(notification, projectNames);

  if (!formatter) {
    // biome-ignore lint/suspicious/noConsole: console is ok here
    console.error(notification);
    throw new Error('Not implemented');
  }

  let formatted: ReturnType<NonNullable<typeof formatter>>;
  try {
    formatted = formatter(notification, projectNames);
  } catch (error) {
    // biome-ignore lint/suspicious/noConsole: keep bad notification payloads visible without crashing UI
    console.error('Unable to format notification', notification, error);
    return formatFallbackNotification(notification, userEmail);
  }

  const { link, subject, message, possessive } = formatted;

  return {
    link,
    subject,
    notif_content: `${formatUserStr(notification.created_by, {
      userEmail,
      possessive,
    })} ${message}`,
    message: error ?? null,
    whenStr: dayjs(notification.created_at).fromNow(),
  };
};

export const formatNotifications = (
  res: NotificationsQueryResult,
  projectIdMap: ObjectIdMap | undefined,
  userEmail: string | undefined,
) => {
  if (!projectIdMap) {
    return notLoadedDefault;
  }

  const notifs = res.rows.map((n) => {
    const { meta, ...rest } = n;
    return {
      ...rest,
      ...formatNotification(n, projectIdMap, userEmail),
    };
  });

  const unreadNotifs = res.unread.map((n) => {
    const prevFormatted = find(notifs, ({ id }) => id === n.id);
    if (prevFormatted) return prevFormatted;
    const { meta, ...rest } = n;
    return {
      ...rest,
      ...formatNotification(n, projectIdMap, userEmail),
    };
  });

  return { ...res, unread: unreadNotifs, rows: orderBy(notifs, 'created_at', 'desc') };
};

export const getNotifSx = (theme: Theme, notif: UINotification) => {
  const { severity, viewed_at } = notif;
  const viewed = Boolean(viewed_at);
  const color = theme.palette[severity].main;
  return {
    backgroundColor: !viewed ? alpha(color, 0.1) : undefined,
    '&:hover': {
      backgroundColor: alpha(color, 0.2),
    },
  };
};
